import { customerOverrideForDrilldown } from '@src/components/app-viewer-sessions/utils';
import {
  useGetClientIdsByViewerId,
  useGetTimelineClientIds,
  useGetTimelineViewerIds,
  useGetViewerIdsByClientId,
} from '../../utils/queryHooks';
import { USER_ID_FIELD } from '../../constants';
import { IDSelectorProps } from '@conviva/organism-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateTempDashboardByTitle,
  createTempDashboard,
  removeKey,
} from '@store/tempDashboard/actions';
import { useNavigate } from 'react-router-dom';
import usePrevious from '@hooks/usePrevious';
import { getAppViewerModuleTempDashboardUrl } from '@src/components/common/Dashboard/utils/getTempDashboardUrl';
import { DASHBOARD_TYPE } from '@src/utils/constants';
import { EQueryIdType } from '@src/pages/AppTimeline/types';
import { idOptions } from '@src/pages/AppTimeline/constants';
import { getClientIdSuffix } from '../..';

export const useIds = ({
  id,
  flowId,
  flowIdentifier,
  viewerId,
  clientId,
  selectedTrendMetric,
  interval,
  customer_id,
  idType,
  handleIdSelectChanged,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedViewerId, setSelectedViewerId] = useState<string[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);
  const userIdKey = customerOverrideForDrilldown[customer_id] ?? USER_ID_FIELD;

  useEffect(() => {
    if (clientId) {
      setSelectedClientId(clientId);
    }
    if (viewerId) {
      if (Array.isArray(viewerId) && viewerId.length > 0) {
        setSelectedViewerId(viewerId);
      } else {
        setSelectedViewerId([viewerId]);
      }
    }
  }, [clientId, viewerId]);

  const { data: clientIdArrByViewer, isFetching: clientIdLoading } = useGetClientIdsByViewerId(
    idType,
    viewerId,
    selectedTrendMetric,
    interval,
    customer_id,
    flowIdentifier,
  );
  const { data: viewerIdArrByClient, isFetching: viewerIdLoading } = useGetViewerIdsByClientId(
    idType,
    clientId,
    selectedTrendMetric,
    interval,
    customer_id,
    flowIdentifier,
  );
  const { data: clientIdArr } = useGetTimelineClientIds(
    idType,
    selectedTrendMetric,
    interval,
    flowIdentifier,
  );
  const { data: viewerIdArr } = useGetTimelineViewerIds(
    idType,
    selectedTrendMetric,
    interval,
    customer_id,
    flowIdentifier,
  );

  const genDashBoardTitle = (id: string, type: EQueryIdType) => {
    if (type === EQueryIdType.userId) {
      return `User - ${id}`;
    }
    if (type === EQueryIdType.clientId) {
      return `Client - ${id}`;
    }
    return '';
  };
  let dashboardTitle = '';
  if (viewerId) {
    dashboardTitle = genDashBoardTitle(viewerId, EQueryIdType.userId);
  } else if (clientId) {
    dashboardTitle = genDashBoardTitle(clientId, EQueryIdType.clientId);
  }
  const prevDashboardTitle = usePrevious(dashboardTitle);

  const handleSubSelectChange = useCallback(
    (v: string): void => {
      if (id) {
        dispatch(removeKey(id));
      }
      const redirectHandler = ({ id: tabId }) => {
        navigate(getAppViewerModuleTempDashboardUrl({ viewerId, clientId: v, flowId }, tabId));
      };
      dispatch(
        createTempDashboard(
          DASHBOARD_TYPE.APP_VIEWER_MODULE,
          { viewerId, clientId: v, flowId },
          dashboardTitle,
          {
            redirectHandler,
          },
        ),
      );
      setSelectedClientId(v);
    },
    [dashboardTitle, dispatch, navigate, id, viewerId, flowId],
  );

  useEffect(() => {
    if (Array.isArray(clientIdArrByViewer) && clientIdArrByViewer.length > 0) {
      const firstClientId = clientIdArrByViewer[0]?.client_id;
      if (firstClientId && !clientId) {
        handleSubSelectChange(firstClientId);
        setSelectedClientId(firstClientId);
      }
    }
  }, [clientId, clientIdArrByViewer, handleSubSelectChange]);

  useEffect(() => {
    if (
      !viewerId &&
      idType === EQueryIdType.clientId &&
      Array.isArray(viewerIdArrByClient) &&
      viewerIdArrByClient.length > 0
    ) {
      const selected = viewerIdArrByClient.map((e) => e.value[0]);
      setSelectedViewerId(selected);
    }
  }, [idType, userIdKey, viewerId, viewerIdArrByClient]);

  useEffect(() => {
    if (idType === EQueryIdType.userId) {
      setDeviceOptions(
        clientIdArrByViewer?.map((e) => ({
          label: e.device_name,
          value: e.client_id,
        })) ?? [],
      );
      return;
    } else if (idType === EQueryIdType.clientId) {
      setDeviceOptions(
        clientIdArr?.map((e) => ({
          label: e.device_name,
          value: e.client_id,
        })) ?? [],
      );
      return;
    }
  }, [clientIdArr, clientIdArrByViewer, idType]);

  const updateUrlAndTabName = (v: string, type: EQueryIdType): void => {
    const arr: string[] = window.location.href.split('?');
    let title = '';
    if (type === EQueryIdType.userId) {
      title = 'userId';
    } else if (type === EQueryIdType.clientId) {
      title = 'clientId';
    }
    const BASE_URL_INDEX = 0;
    const res = `${arr[BASE_URL_INDEX]}?${title}=${encodeURIComponent(v)}`;
    window.history.replaceState({}, document.title, res);
  };

  const handleMainSelectChange = (v: string): void => {
    let newViewerId = '';
    let newClientId = '';
    let newDashboardTitle = '';

    if (idType === EQueryIdType.userId) {
      newViewerId = v;
      newDashboardTitle = genDashBoardTitle(v, EQueryIdType.userId);
      setSelectedViewerId([v]);
      setSelectedClientId('');
    } else if (idType === EQueryIdType.clientId) {
      newClientId = v;
      newDashboardTitle = genDashBoardTitle(v, EQueryIdType.clientId);
      setSelectedClientId(v);
      setSelectedViewerId([]);
    }

    // eslint-disable-next-line no-use-before-define
    if (v && id !== 'user-timeline') {
      updateUrlAndTabName(v, idType);
    }

    dispatch(
      updateTempDashboardByTitle(prevDashboardTitle, newDashboardTitle, {
        viewerId: newViewerId,
        clientId: newClientId,
      }),
    );
    if (id) {
      dispatch(removeKey(id));
    }
    const redirectHandler = ({ id: tabId }) => {
      navigate(
        getAppViewerModuleTempDashboardUrl(
          { viewerId: newViewerId, clientId: newClientId, flowId },
          tabId,
        ),
      );
    };
    dispatch(
      createTempDashboard(
        DASHBOARD_TYPE.APP_VIEWER_MODULE,
        { viewerId: newViewerId, clientId: newClientId, flowId },
        newDashboardTitle,
        {
          redirectHandler,
        },
      ),
    );
  };

  const userIdOptions = useMemo(
    () =>
      viewerIdArr?.map((e: string) => ({
        label: e,
        value: e,
      })) ?? [],
    [viewerIdArr],
  );

  const clientIdOptions = useMemo(
    () =>
      clientIdArr?.map((e) => ({
        label: e.client_id,
        value: e.client_id,
      })) ?? [],
    [clientIdArr],
  );

  const clientIdArrByViewerOptions = useMemo(
    () =>
      clientIdArrByViewer?.map((e) => ({
        label: e.client_id,
        value: e.client_id,
      })) ?? [],
    [clientIdArrByViewer],
  );

  const viewerIdArrByClientOptions = useMemo(
    () =>
      viewerIdArrByClient?.map((e) => ({
        label: e[userIdKey],
        value: e.value[0],
      })) ?? [],
    [userIdKey, viewerIdArrByClient],
  );

  const filterOption = (
    input: string,
    option?: { label: string; value: string | number },
  ): boolean => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  const handleTitleChange = (v: string) => {
    handleIdSelectChanged(v);
  };

  const res: IDSelectorProps[] = [];
  if (idType === EQueryIdType.userId) {
    res.push(
      {
        titleValue: EQueryIdType.userId,
        titleOptions: idOptions,
        titleOnChange: handleTitleChange,
        value: selectedViewerId[0],
        options: userIdOptions,
        onChange: handleMainSelectChange,
        filterOption,
        allowCopy: true,
      },
      {
        titleValue: 'Client ID',
        titleDisabled: true,
        value: selectedClientId,
        options: clientIdArrByViewerOptions,
        onChange: handleSubSelectChange,
        filterOption,
        allowCopy: true,
        titleSuffix: getClientIdSuffix(),
      },
      {
        titleValue: 'Device Name',
        titleDisabled: true,
        value: selectedClientId,
        options: deviceOptions,
        onChange: handleSubSelectChange,
        filterOption,
      },
    );
  } else if (idType === EQueryIdType.clientId) {
    res.push(
      {
        titleValue: EQueryIdType.clientId,
        titleOptions: idOptions,
        titleOnChange: handleTitleChange,
        value: selectedClientId || viewerId,
        options: clientIdOptions,
        onChange: handleMainSelectChange,
        filterOption,
        allowCopy: true,
      },
      {
        titleValue: 'Device Name',
        titleDisabled: true,
        value: viewerIdArrByClient?.[0]?.device_name ?? 'N/A',
        disabled: true,
      },
    );
    if (viewerIdArrByClientOptions.length === 0) {
      res.push({
        titleValue: 'User ID',
        titleDisabled: true,
        value: 'N/A',
        disabled: true,
      });
    } else if (viewerIdArrByClientOptions.length === 1) {
      res.push({
        titleValue: 'User ID',
        titleDisabled: true,
        value: viewerIdArrByClient?.[0]?.[userIdKey],
        disabled: true,
      });
    } else {
      res.push({
        titleValue: 'User ID',
        titleDisabled: true,
        value: selectedViewerId,
        type: 'multi-select',
        options:
          viewerIdArrByClientOptions.length > 0
            ? [
                {
                  options: viewerIdArrByClientOptions,
                  category: 'User ID',
                  hideTitle: true,
                },
              ]
            : [],
        onChange: (s: string[]) => setSelectedViewerId(s),
        multiSelectHeight: 32,
        dropdownPlacement: 'bottomLeft',
        minSelected: 1,
      });
    }
  }

  const shouldShowUserIdToggle = useMemo(() => {
    if (idType === EQueryIdType.clientId && viewerIdArrByClient && viewerIdArrByClient.length > 1) {
      return true;
    }
    return false;
  }, [idType, viewerIdArrByClient]);

  return {
    selectedViewerId,
    selectedClientId,
    idGroupData: res,
    idLoading: clientIdLoading || viewerIdLoading,
    idType,
    shouldShowUserIdToggle,
  };
};
